/**
 * Main wrapper for all "logged-in" pages, including the main nav, header and routes
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';

import classnames from 'classnames';
import Nav from './Nav/Nav';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import Banner from '../Banner/BannerContainer';
import MissionCtrlRoutes from './MissionCtrlRoutes/MissionCtrlRoutes';
import WithSpecifiedTheme from '../WithSpecifiedTheme/WithSpecifiedTheme';
import { lightTheme } from '../App/App.theme';

const propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    nav: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  setUiState: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.func.isRequired,
  user: PropTypes.shape({
    impersonatedOrganizationId: PropTypes.string,
  }).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isORv4Admin: PropTypes.bool.isRequired,
  activeMessage: PropTypes.shape({}),
};

const defaultProps = {
  activeMessage: null,
};

export function UnstyledMissionCtrl({
  classes,
  open,
  setUiState,
  isAuthenticated,
  user,
  isAdmin,
  isORv4Admin,
  activeMessage,
}) {
  let headerClass = classnames(classes.header);
  let navClass = classnames(classes.nav);
  let contentClass = classnames(classes.content);

  if (activeMessage) {
    headerClass = classnames(classes.header, classes.headerPlusCredBanner);
    navClass = classnames(classes.nav, classes.navPlusCredBanner);
    contentClass = classnames(classes.content, classes.contentPlusCredBanner);
  }

  if (user.impersonatedOrganizationId) {
    headerClass = classnames(classes.header, classes.headerPlusImpBanner);
    navClass = classnames(classes.nav, classes.navPlusImpBanner);
    contentClass = classnames(classes.content, classes.contentPlusImpBanner);
  }

  if (user.impersonatedOrganizationId && activeMessage) {
    headerClass = classnames(classes.header, classes.headerPlusImpCredBanner);
    navClass = classnames(classes.nav, classes.navPlusImpCredBanner);
    contentClass = classnames(classes.content, classes.contentPlusImpCredBanner);
  }

  const handleNavToggle = (val) => setUiState({ open: val });
  return (
    <div className={classes.missionCtrlWrapper}>
      <div className={classes.root}>
        <Banner />
        <Header
          className={headerClass}
          isOpen={open}
          handleNavToggle={handleNavToggle}
          user={user}
          isAdmin={isAdmin}
          isORv4Admin={isORv4Admin}
        />
        <Nav
          className={navClass}
          isOpen={open}
          handleNavToggle={handleNavToggle}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
        />
        <div className={contentClass}>
          <MissionCtrlRoutes />
        </div>
      </div>
      <Footer />
    </div>
  );
}

UnstyledMissionCtrl.propTypes = propTypes;
UnstyledMissionCtrl.defaultProps = defaultProps;

const appBar = 60;
const credBanner = 45;
const impBanner = 45;

const styles = (theme) => ({
  missionCtrlWrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  root: {
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    alignItems: 'stretch',
    flexGrow: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    zIndex: theme.zIndex.drawer + 1,
    height: appBar,
    top: 0,
  },
  headerPlusCredBanner: {
    top: credBanner,
  },
  headerPlusImpBanner: {
    top: impBanner,
  },
  headerPlusImpCredBanner: {
    top: impBanner + credBanner,
  },
  nav: {
    flex: '0 0 auto',
    paddingTop: appBar,
    minWidth: '220px',
  },
  navPlusCredBanner: {
    paddingTop: appBar + credBanner,
  },
  navPlusImpBanner: {
    paddingTop: appBar + impBanner,
  },
  navPlusImpCredBanner: {
    paddingTop: appBar + credBanner + impBanner,
  },
  content: {
    position: 'relative',
    flexGrow: 1,
    marginTop: appBar,
    flex: 2,
    maxWidth: '100%',
    minWidth: 0,
    padding: theme.spacing(1),
  },
  contentPlusCredBanner: {
    marginTop: appBar + credBanner,
  },
  contentPlusImpBanner: {
    marginTop: appBar + impBanner,
  },
  contentPlusImpCredBanner: {
    marginTop: appBar + credBanner + impBanner,
  },
  [theme.breakpoints.down('sm')]: {
    header: {
      zIndex: theme.zIndex.modal + 1,
    },
    nav: {
      width: 280,
    },
    logo: {
      height: 30,
    },
  },
});

export default WithSpecifiedTheme(lightTheme)(withStyles(styles)(UnstyledMissionCtrl));
